import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Screen-data cache for the admin and clinician portals — the same idea as the
 * patient/caregiver portal contexts (see CaregiverPatientsContext): data lives
 * above the routed pages, so going back to a screen shows what it already had
 * instantly instead of a spinner, and is then silently revalidated in the
 * background (never a loading flash once something is on screen).
 *
 * The provider is mounted inside each portal's protected layout, so logging
 * out unmounts it and drops everything cached — nothing carries over to the
 * next account in the same tab. Without a provider (e.g. a component rendered
 * on its own in a test) the hook still works, just with a cache private to
 * that component instance, i.e. the old fetch-on-mount behaviour.
 */

interface QueryResult<T, E> {
  data: T | null;
  error: E | null;
}

export class QueryCache {
  private entries = new Map<string, unknown>();
  private inflight = new Map<string, Promise<QueryResult<unknown, unknown>>>();
  private listeners = new Map<string, Set<() => void>>();

  has(key: string): boolean {
    return this.entries.has(key);
  }

  get<T>(key: string): T | undefined {
    return this.entries.get(key) as T | undefined;
  }

  set<T>(key: string, data: T): void {
    this.entries.set(key, data);
    this.notify(key);
  }

  /** Drops every entry whose key starts with `prefix`. Screens currently
   *  showing one refetch it silently (keeping their data on screen until the
   *  fresh copy lands); the rest load fresh the next time they open. */
  invalidate(prefix: string): void {
    const keys = new Set([...this.entries.keys(), ...this.listeners.keys()]);
    for (const key of keys) {
      if (!key.startsWith(prefix)) continue;
      this.entries.delete(key);
      this.notify(key);
    }
  }

  /** One request per key at a time — two screens asking together share it. */
  fetch<T, E>(key: string, fetcher: () => Promise<QueryResult<T, E>>): Promise<QueryResult<T, E>> {
    const running = this.inflight.get(key);
    if (running) return running as Promise<QueryResult<T, E>>;
    const request = fetcher()
      .then((result) => {
        if (result.data !== null) this.set(key, result.data);
        return result;
      })
      .finally(() => this.inflight.delete(key));
    this.inflight.set(key, request as Promise<QueryResult<unknown, unknown>>);
    return request;
  }

  subscribe(key: string, listener: () => void): () => void {
    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    set.add(listener);
    return () => {
      set.delete(listener);
      if (set.size === 0) this.listeners.delete(key);
    };
  }

  private notify(key: string): void {
    this.listeners.get(key)?.forEach((listener) => listener());
  }
}

const QueryCacheContext = createContext<QueryCache | null>(null);

export function QueryCacheProvider({ children }: { children: ReactNode }) {
  const [cache] = useState(() => new QueryCache());
  return <QueryCacheContext.Provider value={cache}>{children}</QueryCacheContext.Provider>;
}

function useQueryCache(): QueryCache {
  const shared = useContext(QueryCacheContext);
  const [local] = useState(() => new QueryCache());
  return shared ?? local;
}

export interface CachedQuery<T, E> {
  /** Cached or freshly fetched data; undefined until the first success. */
  data: T | undefined;
  /** The last failed fetch's error (cleared by the next success). */
  error: E | null;
  /** True only while there's nothing to show yet — never during a background refresh. */
  loading: boolean;
  /** Silent refetch: current data stays on screen until the new copy arrives. */
  refresh: () => Promise<void>;
  /** Writes straight into the cache (e.g. drop a row after an action succeeds). */
  mutate: (updater: (previous: T | undefined) => T) => void;
  /** The cache this query reads from, for writes that span several keys
   *  (e.g. invalidate every filter's list after an action). */
  cache: QueryCache;
}

/**
 * Stale-while-revalidate read of one screen's data. `key` identifies the data
 * (include every parameter the fetcher uses); pass null to fetch nothing.
 * `fetcher` returns the API layer's usual `{ data, error }` result.
 */
export function useCachedQuery<T, E>(
  key: string | null,
  fetcher: () => Promise<QueryResult<T, E>>,
  /** onError's `hadData`: something was already on screen (a background refresh failed). */
  options?: { onError?: (error: E, context: { hadData: boolean }) => void },
): CachedQuery<T, E> {
  const cache = useQueryCache();
  const [data, setData] = useState<T | undefined>(() => (key ? cache.get<T>(key) : undefined));
  const [error, setError] = useState<E | null>(null);
  const [loading, setLoading] = useState(() => key !== null && !cache.has(key));

  // Latest fetcher/onError without re-subscribing on every render.
  const fetcherRef = useRef(fetcher);
  const onErrorRef = useRef(options?.onError);
  fetcherRef.current = fetcher;
  onErrorRef.current = options?.onError;
  const keyRef = useRef(key);
  keyRef.current = key;
  // Whether data is on screen right now — an invalidated entry is gone from the
  // cache but its last copy stays displayed until the refetch lands.
  const shownRef = useRef(data !== undefined);
  shownRef.current = data !== undefined;

  const revalidate = useCallback(async () => {
    if (key === null) return;
    const result = await cache.fetch(key, () => fetcherRef.current());
    // A response for a key this screen has already moved away from is ignored.
    if (keyRef.current !== key) return;
    if (result.data !== null) {
      setError(null);
    } else if (result.error !== null) {
      setError(result.error);
      onErrorRef.current?.(result.error, { hadData: shownRef.current });
    }
    setLoading(false);
  }, [cache, key]);

  useEffect(() => {
    if (key === null) {
      setData(undefined);
      setError(null);
      setLoading(false);
      return;
    }
    setData(cache.get<T>(key));
    setError(null);
    setLoading(!cache.has(key));
    const unsubscribe = cache.subscribe(key, () => {
      if (cache.has(key)) setData(cache.get<T>(key));
      else void revalidate();
    });
    void revalidate();
    return unsubscribe;
  }, [cache, key, revalidate]);

  const mutate = useCallback(
    (updater: (previous: T | undefined) => T) => {
      if (key !== null) cache.set(key, updater(cache.get<T>(key)));
    },
    [cache, key],
  );

  return { data, error, loading, refresh: revalidate, mutate, cache };
}
